import { ClientsApiService } from './crm/ClientsApiService.js';
import { CrmApiRepository } from './crm/CrmApiRepository.js';
import { mapClientFromApi, mapClientToApi } from './crm/ApiEntityMapper.js';
import { generateId, nowIso } from '../utils/IdUtils.js';
import { AccountContactService } from './AccountContactService.js';
import { ContactService } from './ContactService.js';
import { getAccountDefaultSplits } from '../constants/CommissionApplicationTypes.js';
import { auth } from './AuthService.js';
import { AuditService } from './AuditService.js';
import { formatDateDisplay } from '../utils/Utils.js';
import { UserService } from './UserService.js';
import { ActivityService } from './ActivityService.js';
import { CacheService } from './CacheService.js';
import { EMAIL_ERROR_MESSAGE, isBlankOrValidEmail } from '../utils/EmailValidation.js';

export class ClientService {
    static async getAll() {
        return CrmApiRepository.getAllClients();
    }

    static async getLookup() {
        return CrmApiRepository.getAllClientsLookup();
    }

    static async getById(id) {
        const row = await ClientsApiService.getById(id);
        return mapClientFromApi(row);
    }

    /** Soft name check — returns { exists } only (no id/email leakage). */
    static async checkNameDuplicate(name) {
        const row = await ClientsApiService.checkName(name);
        const data = row?.data ?? row ?? {};
        return {
            exists: !!data.exists,
            name: String(name || '').trim(),
        };
    }

    static async getEnrichedAll() {
        // Use cache to avoid redundant fetches
        const cacheKey = 'clients:enriched:all';
        return CacheService.getOrSet(cacheKey, async () => {
            const [clients, contacts, users] = await Promise.all([
                ClientService.getAll(),
                ContactService.getAll(),
                UserService.getLookupUsers().catch(() => [])
            ]);
            const contactMap = Object.fromEntries(contacts.map(c => [c.id, c]));
            const userMap = Object.fromEntries(users.map(u => [u.id, u.name]));
            // Group contacts by clientId once — avoids N× GET /contacts?client_id=
            const contactsByClientId = new Map();
            for (const contact of contacts) {
                const clientId = contact.clientId;
                if (!clientId) continue;
                const list = contactsByClientId.get(clientId) || [];
                list.push(contact);
                contactsByClientId.set(clientId, list);
            }

            return clients.map((client) => {
                const linked = contactsByClientId.get(client.id) || [];
                const spoc = client.primaryContactId
                    ? contactMap[client.primaryContactId]
                    : null;
                const ownerContact = client.ownerContactId
                    ? contactMap[client.ownerContactId]
                    : null;
                return {
                    ...client,
                    contactCount: linked.length,
                    spocName: spoc ? ContactService.fullName(spoc) : '—',
                    ownerContactName: ownerContact ? ContactService.fullName(ownerContact) : '—',
                    ownerUserName: userMap[client.ownerUserId] || '—',
                    relationshipOwnerName: userMap[client.relationshipOwnerUserId] || '—',
                    createdByName: userMap[client.createdBy] || '—',
                    updatedByName: userMap[client.updatedBy] || '—',
                    createdAtDisplay: (client.createdAt ? formatDateDisplay(client.createdAt) : '—') || '—',
                    updatedAtDisplay: (client.updatedAt ? formatDateDisplay(client.updatedAt) : '—') || '—'
                };
            });
        }, 2 * 60 * 1000);
    }

    static normalizeClient(client) {
        const ts = nowIso();
        const splits = Array.isArray(client.accountCommissionSplits)
            ? client.accountCommissionSplits.map(s => ({
                userId: s.userId || '',
                role: s.role || '',
                applicationType: s.applicationType || 'account_default',
                splitPct: Number(s.splitPct) || 0
            }))
            : [];
        const defaultSplits = getAccountDefaultSplits(splits);

        return {
            id: client.id || generateId('client'),
            name: client.name?.trim() || client.client_name?.trim(),
            website: client.website || '',
            industry: client.industry || '',
            address: client.address || client.billingStreet || '',
            status: client.status || 'active',
            primaryContactId: client.primaryContactId || null,
            ownerContactId: client.ownerContactId || null,
            ownerUserId: client.ownerUserId || null,
            relationshipOwnerUserId: client.relationshipOwnerUserId || null,
            phone: client.phone || '',
            email: client.email || '',
            annualRevenue: Number(client.annualRevenue) || 0,
            accountType: client.accountType || '',
            notes: client.notes || '',
            accountSite: client.accountSite || '',
            parentAccountId: client.parentAccountId || null,
            accountNumber: client.accountNumber || '',
            rating: client.rating || '',
            fax: client.fax || '',
            ownership: client.ownership || '',
            employees: Number(client.employees) || 0,
            sicCode: client.sicCode || '',
            billingStreet: client.billingStreet || client.address || '',
            billingCity: client.billingCity || '',
            billingState: client.billingState || '',
            billingCode: client.billingCode || '',
            billingCountry: client.billingCountry || '',
            shippingStreet: client.shippingStreet || '',
            shippingCity: client.shippingCity || '',
            shippingState: client.shippingState || '',
            shippingCode: client.shippingCode || '',
            shippingCountry: client.shippingCountry || '',
            leadSource: client.leadSource || '',
            technologyStack: client.technologyStack || '',
            currentCms: client.currentCms || '',
            currentEcommercePlatform: client.currentEcommercePlatform || '',
            monthlyWebsiteTraffic: client.monthlyWebsiteTraffic || '',
            estimatedProjectValue: Number(client.estimatedProjectValue) || 0,
            lastContactDate: client.lastContactDate || null,
            aiReadinessScore: client.aiReadinessScore != null && client.aiReadinessScore !== ''
                ? Number(client.aiReadinessScore) : null,
            seoAeoGeoOpportunity: client.seoAeoGeoOpportunity || '',
            accountCommissionEnabled: defaultSplits.length > 0,
            accountCommissionSplits: splits,
            createdBy: client.createdBy || null,
            updatedBy: client.updatedBy || null,
            createdAt: client.createdAt || ts,
            updatedAt: ts
        };
    }

    static async ensureContactLinks(accountId, { ownerContactId, primaryContactId } = {}) {
        if (!accountId) return;
        const contactIds = [...new Set([ownerContactId, primaryContactId].filter(Boolean))];
        for (const contactId of contactIds) {
            const contact = await ContactService.getById(contactId);
            if (!contact) continue;
            if (contact.clientId !== accountId) {
                await AccountContactService.link(accountId, contactId, {
                    isPrimary: contactId === primaryContactId
                });
            } else if (contactId === primaryContactId) {
                await AccountContactService.setPrimary(accountId, contactId);
            }
        }
    }

    static async save(client) {
        const prev = client.id ? await ClientService.getById(client.id) : null;
        const session = auth.getSession();
        const record = ClientService.normalizeClient({
            ...client,
            createdBy: prev?.createdBy || client.createdBy || session?.userId || null,
            updatedBy: session?.userId || client.updatedBy || null,
            createdAt: prev?.createdAt || client.createdAt
        });
        if (!record.name) throw new Error('Account name is required.');
        if (!isBlankOrValidEmail(record.email)) throw new Error(EMAIL_ERROR_MESSAGE);

        const apiPayload = mapClientToApi(record);
        let saved;
        if (client.id) {
            window._suppressRealtimeToastFor?.('client.updated', client.id);
            window._suppressRealtimeRefreshFor?.('client.updated', client.id);
            saved = await ClientsApiService.update(client.id, apiPayload);
        } else {
            saved = await ClientsApiService.create(apiPayload);
            const createdId = saved?.id || mapClientFromApi(saved)?.id;
            if (createdId) {
                window._suppressRealtimeToastFor?.('client.created', createdId);
                window._suppressRealtimeRefreshFor?.('client.created', createdId);
            }
        }
        const intendedPrimaryContactId = record.primaryContactId || null;
        const intendedOwnerContactId = record.ownerContactId || null;
        const result = mapClientFromApi(saved);
        Object.assign(record, result);
        // API create/update may omit contact ids — keep the values the form submitted.
        if (!record.primaryContactId && intendedPrimaryContactId) {
            record.primaryContactId = intendedPrimaryContactId;
        }
        if (!record.ownerContactId && intendedOwnerContactId) {
            record.ownerContactId = intendedOwnerContactId;
        }

        if (record.id) {
            try {
                await ClientService.ensureContactLinks(record.id, {
                    ownerContactId: record.ownerContactId || intendedOwnerContactId,
                    primaryContactId: record.primaryContactId || intendedPrimaryContactId
                });
                // Ensure account primary_contact_id is persisted even when create omitted it.
                const primaryId = record.primaryContactId || intendedPrimaryContactId;
                if (primaryId && result?.primaryContactId !== primaryId) {
                    await AccountContactService.setPrimary(record.id, primaryId);
                    record.primaryContactId = primaryId;
                }
            } catch (err) {
                console.warn('Failed to link account contacts:', err);
            }
        }

        if (prev) {
            await AuditService.logFieldChanges({
                entityType: 'client',
                entityId: record.id,
                action: 'account.fields_changed',
                before: prev,
                after: record,
                fields: ['name', 'email', 'phone', 'industry', 'status']
            });
        } else {
            await AuditService.log({
                entityType: 'client',
                entityId: record.id,
                action: 'account.created',
                after: { name: record.name }
            });
        }
        window.dispatchEvent(new CustomEvent('clientSaved', { detail: { client: record } }));
        return record;
    }

    static getCommissionDefaults(account) {
        if (!account?.accountCommissionSplits?.length) return [];
        return getAccountDefaultSplits(account.accountCommissionSplits);
    }

    static async delete(id) {
        const prev = await ClientService.getById(id);
        if (!prev) return;
        window._suppressRealtimeToastFor?.('client.deleted', id);
        window._suppressRealtimeRefreshFor?.('client.deleted', id);
        await ClientsApiService.softDelete(id);
        CrmApiRepository.clearCache();
        await AuditService.log({
            entityType: 'client',
            entityId: id,
            action: 'account.disabled',
            before: { status: prev.status },
            after: { status: 'inactive' }
        });
        window.dispatchEvent(new CustomEvent('clientDeleted', { detail: { id } }));
    }

    static async getActivitySummary(accountId) {
        const cacheKey = `client:${accountId}:activitySummary`;
        return CacheService.getOrSet(cacheKey, async () => {
            const [deals, contacts] = await Promise.all([
                CrmApiRepository.getAllDeals(),
                ContactService.getAll()
            ]);
            const accountDeals = deals.filter(d => d.clientId === accountId);
            const accountContacts = contacts.filter(c => c.clientId === accountId);
            const allSchedules = await CrmApiRepository.getAllSchedules();
            const dealIds = new Set(accountDeals.map(d => d.id));
            const schedules = allSchedules.filter(s => dealIds.has(s.dealId));
            return {
                dealCount: accountDeals.length,
                contactCount: accountContacts.length,
                scheduleCount: schedules.length,
                totalValue: accountDeals.reduce((s, d) => s + (d.amount || 0), 0)
            };
        }, 2 * 60 * 1000);
    }

    static async getWithContacts(id) {
        const client = await ClientService.getById(id);
        if (!client) return null;
        const contacts = await AccountContactService.getContactsForAccount(id);
        return { client, contacts };
    }

    static async setPrimaryContact(accountId, contactId) {
        await AccountContactService.setPrimary(accountId, contactId);
    }

    static async getAccount360Summary(accountId) {
        const cacheKey = `client:${accountId}:360summary`;
        return CacheService.getOrSet(cacheKey, async () => {
            const account = await ClientService.getById(accountId);
            if (!account) return null;

            const [deals, contacts, activities] = await Promise.all([
                CrmApiRepository.getAllDeals(),
                AccountContactService.getContactsForAccount(accountId),
                ActivityService.getForEntity('client', accountId)
            ]);

            const accountDeals = deals.filter(d => d.clientId === accountId);
            const openDeals = accountDeals.filter(d => d.status === 'open');
            const wonDeals = accountDeals.filter(d => d.status === 'won');
            const openPipelineValue = openDeals.reduce((s, d) => s + (d.dealAmount || d.amount || 0), 0);
            const year = new Date().getFullYear();
            const allSchedules = await CrmApiRepository.getAllSchedules();
            const revenueYtd = allSchedules
                .filter(s => {
                    const deal = accountDeals.find(d => d.id === s.dealId);
                    return deal && s.status === 'paid' && String(s.invoiceDate || '').startsWith(String(year));
                })
                .reduce((sum, s) => sum + (s.paidAmount || s.plannedAmount || 0), 0);
            const spoc = contacts.find(c => c.isPrimary) || contacts[0];
            const lastActivity = [...activities].sort((a, b) =>
                (b.createdAt || '').localeCompare(a.createdAt || '')
            )[0];

            const allSplits = account.accountCommissionSplits || [];
            const defaultSplits = getAccountDefaultSplits(allSplits);

            return {
                openDealsCount: openDeals.length,
                wonDealsCount: wonDeals.length,
                openPipelineValue,
                revenueYtd,
                spocName: spoc ? ContactService.fullName(spoc) : '—',
                lastContactDate: account.lastContactDate || lastActivity?.createdAt?.split('T')[0] || null,
                rating: account.rating || '',
                commissionEnabled: allSplits.length > 0,
                commissionTotalPct: allSplits.reduce((s, r) => s + (Number(r.splitPct) || 0), 0),
                hasDealDefaults: defaultSplits.length > 0,
                estimatedProjectValue: Number(account.estimatedProjectValue) || 0
            };
        }, 2 * 60 * 1000);
    }
}
